import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import cn from 'classnames';
import { useSharedTranscript } from '@utils/providers/shared-transcript-context.tsx';
import { Info, Redo2, RotateCcw, Save, Undo2 } from 'lucide-react';
import SaveStatus from '@components/atoms/ui-kit/save-status.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import { shortcutLabel } from '@utils/editors/shortcut-label.tsx';

type Props = {
  setShowInfo: (show: boolean) => void;
  enabled: boolean;
  setEnabled: Dispatch<SetStateAction<boolean>>;
};

export const SharedTranscriptActionButtons = ({
  setShowInfo,
  enabled
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { cls } = useThemeVars();

  const {
    saveDocument,
    saveDocumentLoading,
    undo,
    redo,
    reset,
    undoStack,
    redoStack,
    isDirty,
    documentResetting
  } = useSharedTranscript();

  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [transientSaving, setTransientSaving] = useState(false);
  const prevDirtyRef = useRef<boolean>(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    const prev = prevDirtyRef.current;
    const now = !!isDirty;

    if (prev && !now) {
      setLastSavedAt(Date.now());
      setTransientSaving(true);
      const id = setTimeout(() => setTransientSaving(false), 700);
      return () => clearTimeout(id);
    }

    prevDirtyRef.current = now;
  }, [isDirty]);

  const isSaving = useMemo(
    () => Boolean(saveDocumentLoading || transientSaving),
    [saveDocumentLoading, transientSaving]
  );

  const handleReset = async () => {
    setIsResetting(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    reset();
    setIsResetting(false);
    setShowResetModal(false);
  };

  const iconBtnClass = cn(
    'flex items-center justify-center h-9 w-9 transition-colors hover:opacity-80 rounded-2xl border shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:opacity-40',
    cls.cardBg
  );
  const iconBtnStyle = { borderColor: getColour('border') };

  return (
    <>
      <div className="flex items-center gap-1">
        <Tooltip label={t('keyboard-shortcuts')}>
          <button
            type="button"
            onClick={() => setShowInfo(true)}
            aria-label={t('keyboard-shortcuts')}
            className={iconBtnClass}
            style={iconBtnStyle}
          >
            <Info size={14} style={{ color: getColour('icon') }} />
          </button>
        </Tooltip>

        <Tooltip label={shortcutLabel(t('undo'), 'Ctrl+Z')}>
          <button
            type="button"
            onClick={() => undo()}
            disabled={saveDocumentLoading || undoStack.length === 0}
            aria-label={t('undo')}
            className={iconBtnClass}
            style={iconBtnStyle}
          >
            <Undo2 size={14} style={{ color: getColour('icon') }} />
          </button>
        </Tooltip>

        <Tooltip label={shortcutLabel(t('redo'), 'Ctrl+Y')}>
          <button
            type="button"
            onClick={() => redo()}
            disabled={saveDocumentLoading || redoStack.length === 0}
            aria-label={t('redo')}
            className={iconBtnClass}
            style={iconBtnStyle}
          >
            <Redo2 size={14} style={{ color: getColour('icon') }} />
          </button>
        </Tooltip>

        <Tooltip label={t('reset')}>
          <button
            type="button"
            onClick={() => setShowResetModal(true)}
            disabled={saveDocumentLoading || documentResetting || enabled}
            aria-label={t('reset')}
            className={iconBtnClass}
            style={iconBtnStyle}
          >
            <RotateCcw size={14} style={{ color: getColour('icon') }} />
          </button>
        </Tooltip>

        <Tooltip label={shortcutLabel(t('save'), 'Ctrl+S')}>
          <button
            type="button"
            onClick={() => saveDocument(true)}
            disabled={saveDocumentLoading || enabled}
            aria-label={t('save')}
            className={iconBtnClass}
            style={iconBtnStyle}
          >
            <Save size={14} style={{ color: getColour('icon') }} />
          </button>
        </Tooltip>

        <SaveStatus
          isSaving={isSaving}
          isEdited={Boolean(isDirty)}
          lastSavedAt={lastSavedAt}
        />
      </div>

      <ConfirmationModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onConfirm={handleReset}
        size="small"
        title={t('reset-confirmation')}
        caption={t('reset-confirmation-desc')}
        confirmLabel={isResetting ? t('reseting') : t('reset')}
        cancelLabel={t('cancel')}
        confirmDisabled={isResetting}
        destructive={false}
        autoFocusConfirm
      />
    </>
  );
};
